import { Participation, Person } from '@amzn/red-velvet-api';
import { useSessionStorage } from 'usehooks-ts';
import { Amazonian } from '../../utils/attendance/peopleSearch';

export const BULK_PARTICIPANTS_KEY = 'bulk-participants';

export interface BulkParticipantData {
  [key: string]: Amazonian;
}

export interface BulkParticipantsSearchParams {
  bulkParticipants: BulkParticipantData;
  updateBulkParticipants: (participants: BulkParticipantData) => void;
  updateBulkParticipantsFromParticipantData: (participantData: {
    [key: string]: { attendance?: string; trackedTime?: number };
  }) => void;
  resetBulkParticipants: () => void;
}

export function convertToBulkParticipantData(
  participations: Participation[],
  peopleWithoutParticipation: Person[],
  userId: string
): BulkParticipantData {
  const result: BulkParticipantData = {};

  (participations || []).forEach((p) => {
    if (p.volunteerAlias) {
      result[p.volunteerAlias] = {
        alias: p.volunteerAlias,
        name: `${p.volunteerLastName}, ${p.volunteerFirstName}`,
        building: p.volunteerBuildingName,
        managerLogin: p.volunteerManagerAlias,
        trackedBy: p.attendanceUpdatedByAlias,
        signedUp: p.isSignedUp,
        attendance: p.attendanceStatus || 'ATTENDED',
        trackedTime: p.attendanceMinutes || 0,
      };
    }
  });

  (peopleWithoutParticipation || []).forEach((p) => {
    if (p.alias) {
      result[p.alias] = {
        alias: p.alias,
        name: `${p.lastName}, ${p.firstName}`,
        building: p.building,
        managerLogin: p.managerLogin,
        signedUp: false,
        attendance: 'ATTENDED',
        trackedTime: 0,
        trackedBy: userId,
      };
    }
  });

  return result;
}

export function convertParticipantDataToBulkParticipantData(
  participantData: { [key: string]: { attendance?: string; trackedTime?: number } },
  existingBulkParticipants: BulkParticipantData
): BulkParticipantData {
  const result: BulkParticipantData = {};
  Object.entries(participantData).forEach(([alias, data]) => {
    if (existingBulkParticipants[alias]) {
      result[alias] = { ...existingBulkParticipants[alias], ...data };
    }
  });
  return result;
}

export function useBulkParticipantSessionStorage(): BulkParticipantsSearchParams {
  const [bulkParticipants, setBulkParticipants] = useSessionStorage<BulkParticipantData>(
    BULK_PARTICIPANTS_KEY,
    {}
  );

  const updateBulkParticipants = (updatedParticipants: BulkParticipantData) => {
    const newParticipantsData = { ...bulkParticipants };
    for (const key in updatedParticipants) {
      newParticipantsData[key] = {
        ...newParticipantsData[key],
        ...updatedParticipants[key],
      };
    }

    // Remove participant data that are set to DID_NOT_ATTEND
    // Only delete if: not signed up AND was manually added (not from API)
    // For event-level (composite keys), we rely on the fact that API participations
    // are not in bulkParticipants initially, so if the key exists in bulkParticipants
    // before this update, it was manually added
    for (const key in updatedParticipants) {
      const wasManuallyAdded = bulkParticipants[key] !== undefined;
      if (
        updatedParticipants[key].attendance === 'DID_NOT_ATTEND' &&
        newParticipantsData[key]?.signedUp !== true &&
        wasManuallyAdded
      ) {
        delete newParticipantsData[key];
      }
    }

    if (Object.keys(newParticipantsData).length === 0) {
      setBulkParticipants({});
    } else {
      setBulkParticipants(newParticipantsData);
    }
  };

  const updateBulkParticipantsFromParticipantData = (participantData: {
    [key: string]: { attendance?: string; trackedTime?: number };
  }) => {
    const bulkUpdates = convertParticipantDataToBulkParticipantData(
      participantData,
      bulkParticipants
    );
    updateBulkParticipants(bulkUpdates);
  };

  const resetBulkParticipants = () => {
    setBulkParticipants({});
  };

  return {
    bulkParticipants,
    updateBulkParticipants,
    updateBulkParticipantsFromParticipantData,
    resetBulkParticipants,
  };
}
